import { useState } from 'react'
import { ActionIcon, Badge, Group, Pagination, Stack, Table, Text } from '@mantine/core'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import { FilterChip } from '@lighthouse/design'
import { NOT_MEASURED, agoLabel, exactNumber } from '../../utils/format'
import { SeriesInputs } from './BucketDetail'
import { TownCrierLink } from './TownCrierLink'
import {
  costSummary, isReport, kindLabel, placementLabel, timeLabel, wallClock, dayLabel, wallClockIn,
} from './scheduleFormat'
import type { ScheduleLoadItem, ScheduleLoadSeries } from '../../types/scheduleLoad'

const PAGE_SIZE = 25

type SeriesFilter = 'all' | 'reports' | 'jobs' | 'unplaceable' | 'unknown'

const FILTERS: { value: SeriesFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'reports', label: 'Report deliveries' },
  { value: 'jobs', label: 'Jobs' },
  { value: 'unplaceable', label: 'Cannot be placed' },
  { value: 'unknown', label: 'Unknown cost' },
]

/**
 * Every series the forecast considered — placed or not — with its inputs.
 *
 * The bucket view answers "what is in 15:00"; this answers "what is scheduled at
 * all", which is the other half of the user's question ("what those reports
 * are"). Unplaceable series are here too, with the reason, because a schedule
 * that cannot be placed is still demand the page is honest about not seeing:
 * a worker job fires anyway, at an hour nobody here can compute.
 *
 * Filtered client-side, which is legitimate here and nowhere on `/connections`:
 * `series` is the complete set, not a page of it, so a count under a filter is
 * a count of everything that matches.
 */
export function SeriesTable({
  series, firstListed, workerIanaId, townCrierPath,
}: {
  series: ScheduleLoadSeries[]
  firstListed: Map<string, ScheduleLoadItem>
  workerIanaId: string | null
  townCrierPath: string
}) {
  const [filter, setFilter] = useState<SeriesFilter>('all')
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<string | null>(null)

  const matches = (s: ScheduleLoadSeries, f: SeriesFilter) => {
    switch (f) {
      case 'reports': return isReport(s.kind)
      case 'jobs': return !isReport(s.kind)
      case 'unplaceable': return s.placement !== 'Placed'
      case 'unknown': return s.cost.status === 'Unknown'
      default: return true
    }
  }

  const count = (f: SeriesFilter) => series.filter((s) => matches(s, f)).length
  const rows = series.filter((s) => matches(s, filter))
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const current = Math.min(page, totalPages)
  const visible = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE)

  if (series.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No active report schedule and no recurring job that will fire. Every ETL job is manual-only
        (<code>0 0 31 2 *</code>, 31 February) and contributes nothing; the maintenance chain appears here once
        Automaton has published its catalogue.
      </Text>
    )
  }

  return (
    <Stack gap="sm">
      {/*
        Chips, not a SegmentedControl: five labels with counts need ~500px, and a
        SegmentedControl cannot wrap, so at phone width it clipped the very counts
        that make the filter worth having. The design system's FilterChip wraps.
      */}
      <Group gap={6} role="group" aria-label="Filter the series">
        {FILTERS.map(({ value, label }) => (
          <FilterChip
            key={value}
            active={filter === value}
            onClick={() => {
              setFilter(value)
              setPage(1)
              setExpanded(null)
            }}
          >
            {label} ({exactNumber(count(value))})
          </FilterChip>
        ))}
      </Group>

      {rows.length === 0 ? (
        <Text size="sm" c="dimmed">No series match this filter.</Text>
      ) : (
        <>
          <Table.ScrollContainer minWidth={1100}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40} />
                  <Table.Th>What</Table.Th>
                  <Table.Th>Runs as</Table.Th>
                  <Table.Th>Schedule</Table.Th>
                  <Table.Th>Placement</Table.Th>
                  <Table.Th>Cost per run</Table.Th>
                  <Table.Th ta="right">In window</Table.Th>
                  <Table.Th>First listed, here</Table.Th>
                  <Table.Th>Last run</Table.Th>
                  <Table.Th>Change it</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visible.map((s) => (
                  <SeriesRow
                    key={s.seriesKey}
                    series={s}
                    first={firstListed.get(s.seriesKey)}
                    workerIanaId={workerIanaId}
                    townCrierPath={townCrierPath}
                    isExpanded={expanded === s.seriesKey}
                    onToggle={() => setExpanded(expanded === s.seriesKey ? null : s.seriesKey)}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {totalPages > 1 && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Page {current} of {totalPages} · {exactNumber(rows.length)} series
              </Text>
              <Pagination value={current} total={totalPages} onChange={setPage} />
            </Group>
          )}
        </>
      )}
    </Stack>
  )
}

function SeriesRow({
  series, first, workerIanaId, townCrierPath, isExpanded, onToggle,
}: {
  series: ScheduleLoadSeries
  first: ScheduleLoadItem | undefined
  workerIanaId: string | null
  townCrierPath: string
  isExpanded: boolean
  onToggle: () => void
}) {
  const report = isReport(series.kind)
  const firstWall = first ? wallClock(first.startsAt) : null
  const registered = !report && first ? wallClockIn(first.startsAtUtc, workerIanaId) : null

  return (
    <>
      <Table.Tr style={{ cursor: 'pointer' }} onClick={onToggle}>
        <Table.Td>
          <ActionIcon
            variant="subtle"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Hide' : 'Show'} the inputs for ${series.name}`}
            onClick={(event) => {
              event.stopPropagation()
              onToggle()
            }}
          >
            {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
        </Table.Td>
        <Table.Td>
          <Text size="sm" fw={500}>{series.name}</Text>
          <Text size="xs" c="dimmed">
            {report ? (series.reportName ?? 'Report name not recorded') : kindLabel(series.kind)}
            {series.willRefuseToStart && ' · will refuse to start (ETL halted)'}
          </Text>
        </Table.Td>
        <Table.Td>
          {report
            ? <Text size="sm">{series.runAsDisplayName ?? NOT_MEASURED}</Text>
            : <Text size="sm" c="dimmed">Automaton</Text>}
        </Table.Td>
        <Table.Td>
          <Text size="sm">{series.cronDescription || NOT_MEASURED}</Text>
          <Text size="xs" c="dimmed">
            {report ? 'in ' : 'in the worker’s zone, '}
            {series.sourceZoneId ?? 'no zone recorded'}
          </Text>
        </Table.Td>
        <Table.Td>
          {series.placement === 'Placed' ? (
            <Text size="sm">Placed</Text>
          ) : (
            <Stack gap={2}>
              <Badge variant="outline" color={series.firesEvenThoughUnplaceable ? 'orange' : 'gray'} size="sm">
                {placementLabel(series.placement)}
              </Badge>
              <Text size="xs" c="dimmed">
                {series.firesEvenThoughUnplaceable ? 'fires anyway, hour unknown' : 'probably does not fire'}
              </Text>
            </Stack>
          )}
        </Table.Td>
        <Table.Td>
          {series.cost.status === 'Unknown' ? (
            <Stack gap={2}>
              <Badge variant="outline" color="gray" size="sm">Unknown</Badge>
              <Text size="xs" c="dimmed" lineClamp={2}>{series.cost.unknownReason}</Text>
            </Stack>
          ) : (
            <Stack gap={2}>
              <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{costSummary(series.cost)}</Text>
              <Text size="xs" c={series.cost.historyExcludesFailedAttempts ? 'orange' : 'dimmed'}>
                {exactNumber(series.cost.sampleCount)} samples
                {series.cost.historyExcludesFailedAttempts && ' · a floor: last run failed'}
              </Text>
            </Stack>
          )}
        </Table.Td>
        <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          <Text size="sm">{exactNumber(series.occurrencesInWindow)}</Text>
          {(series.truncated || series.notExpanded || series.lookbackTruncated) && (
            <Text size="xs" c="orange">
              {series.notExpanded ? 'not expanded' : series.truncated ? 'cut short — a floor' : 'lookback cut short'}
            </Text>
          )}
        </Table.Td>
        <Table.Td style={{ whiteSpace: 'nowrap' }}>
          {first && firstWall ? (
            <>
              <Text size="sm">{timeLabel(first.startsAt)} {dayLabel(firstWall.date)}</Text>
              {registered && <Text size="xs" c="dimmed">{registered} in the worker&rsquo;s zone</Text>}
            </>
          ) : (
            <Text size="sm" c="dimmed">
              {series.occurrencesInWindow > 0 ? 'not listed' : 'none in window'}
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          {series.lastRunAt == null && series.lastRunStatus == null ? (
            <Text size="sm" c="dimmed">{report ? NOT_MEASURED : 'not recorded here'}</Text>
          ) : (
            <>
              <Text size="sm">{series.lastRunStatus ?? 'status not recorded'}</Text>
              <Text size="xs" c="dimmed">{agoLabel(series.lastRunAt)}</Text>
            </>
          )}
        </Table.Td>
        <Table.Td onClick={(event) => event.stopPropagation()}>
          {report
            ? <TownCrierLink path={series.townCrierPath ?? townCrierPath} scheduleName={series.name} compact />
            : <Text size="xs" c="dimmed">Automaton&rsquo;s own schedule</Text>}
        </Table.Td>
      </Table.Tr>

      {isExpanded && (
        <Table.Tr>
          <Table.Td colSpan={10} style={{ backgroundColor: 'var(--color-bg-inset, transparent)' }}>
            <SeriesInputs series={series} />
          </Table.Td>
        </Table.Tr>
      )}
    </>
  )
}

